import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'motion/react';
import { 
  Sparkles, 
  ShieldCheck, 
  Printer, 
  Layers, 
  Box, 
  CheckCircle2, 
  ChevronRight, 
  Lock, 
  ExternalLink,
  Cpu,
  RefreshCw,
  Volume2,
  VolumeX,
  Heart,
  Star,
  Bot,
  Rocket,
  Wand2,
  Gem,
  Sun,
  Moon,
  Smile,
  Flame,
  Palette,
  Edit3
} from 'lucide-react';
import { 
  FULL_COPYRIGHT_TEXT, 
  SHORT_COPYRIGHT_NOTICE, 
  SECURITY_SIGNATURE_ID, 
  renderCanvasWatermark, 
  setupAntiTamperGuard
} from '../../utils/securityCopyrightGuard';
import { playfulAudio } from '../../utils/playfulAudio';
import { useSaaSContext } from '../../store/SaaSContext';
import { AppLogo } from './AppLogo';

interface SplashScreenProps {
  onFinish?: () => void;
}

const LOADING_STAGES = [
  { threshold: 20, label: 'Warming up Vector Imposition Engine & Font Matrix...', icon: Cpu, badge: 'Phase 1' },
  { threshold: 45, label: 'Mixing PBR Shaders: Holographic Foil, Gold, Spot UV & Chrome...', icon: Sparkles, badge: 'Phase 2' },
  { threshold: 72, label: 'Synthesizing Variable Barcodes, QR Matrix & Data Pipelines...', icon: Layers, badge: 'Phase 3' },
  { threshold: 92, label: 'Calibrating Real-Time 3D Tactile Physics & Packaging Mockups...', icon: Box, badge: 'Phase 4' },
  { threshold: 100, label: 'Cryptographic Security Verified • Studio Ready!', icon: ShieldCheck, badge: 'Ready' }
];

interface FinishPreset {
  id: string;
  name: string;
  gradient: string;
  ring: string;
  textColor: string;
  tag: string;
  foilColor: string;
  accent: string;
}

const STICKER_FINISH_PRESETS: FinishPreset[] = [
  { id: 'gold', name: '24K Gold Foil', gradient: 'from-amber-300 via-yellow-500 to-amber-600', ring: 'ring-amber-400', textColor: 'text-amber-300', tag: '24K Gold', foilColor: '#f59e0b', accent: 'bg-amber-400' },
  { id: 'holographic', name: 'Laser Holographic', gradient: 'from-cyan-400 via-purple-500 to-pink-500', ring: 'ring-cyan-400', textColor: 'text-cyan-300', tag: 'Holo Sheen', foilColor: '#06b6d4', accent: 'bg-cyan-400' },
  { id: 'rosegold', name: 'Rose Gold Metallic', gradient: 'from-rose-300 via-pink-400 to-amber-300', ring: 'ring-rose-400', textColor: 'text-rose-300', tag: 'Rose Gold', foilColor: '#fb7185', accent: 'bg-rose-400' },
  { id: 'chrome', name: 'Chrome Liquid Metal', gradient: 'from-slate-200 via-zinc-400 to-slate-300', ring: 'ring-slate-300', textColor: 'text-slate-200', tag: 'Liquid Chrome', foilColor: '#94a3b8', accent: 'bg-slate-300' },
  { id: 'neon', name: 'Spot UV Neon Glow', gradient: 'from-emerald-400 via-teal-400 to-cyan-500', ring: 'ring-emerald-400', textColor: 'text-emerald-300', tag: 'Spot Neon', foilColor: '#10b981', accent: 'bg-emerald-400' },
  { id: 'galaxy', name: 'Galaxy Cosmic Violet', gradient: 'from-indigo-400 via-purple-500 to-fuchsia-500', ring: 'ring-purple-400', textColor: 'text-purple-300', tag: 'Galaxy Purple', foilColor: '#a855f7', accent: 'bg-purple-400' }
];

const CUTE_EMBLEMS = [
  { id: 'printer', name: 'Print Magic', icon: Printer },
  { id: 'bot', name: 'Cute Robo', icon: Bot },
  { id: 'heart', name: 'Love Print', icon: Heart },
  { id: 'star', name: 'Super Star', icon: Star },
  { id: 'rocket', name: 'Turbo VDP', icon: Rocket },
  { id: 'gem', name: 'Diamond Gem', icon: Gem },
  { id: 'flame', name: 'Hot Drop', icon: Flame },
  { id: 'wand', name: 'Design Wand', icon: Wand2 }
];

const STICKER_SHAPES = [
  { id: 'rounded', name: 'Badge', radius: 'rounded-3xl' },
  { id: 'circle', name: 'Round', radius: 'rounded-full' },
  { id: 'squircle', name: 'Squircle', radius: 'rounded-[34px]' }
];

const QUICK_TAG_SUGGESTIONS = [
  '✨ VIP LUXE',
  '🤖 ROBO PACK',
  '🌿 100% ORGANIC',
  '🚀 TURBO PRINT',
  '💖 HANDMADE',
  '🔥 HOT DROP'
];

interface Particle {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  symbol: string;
}

export const SplashScreen: React.FC<SplashScreenProps> = ({ onFinish }) => {
  const { theme, toggleTheme } = useSaaSContext();
  const [progress, setProgress] = useState<number>(0);
  const [activeFinishIndex, setActiveFinishIndex] = useState<number>(0);
  const [activeEmblemIndex, setActiveEmblemIndex] = useState<number>(0);
  const [activeShapeIndex, setActiveShapeIndex] = useState<number>(0);
  const [customText, setCustomText] = useState<string>('✨ VIP LUXE');
  const [isEditingText, setIsEditingText] = useState<boolean>(false);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [isAudioMuted, setIsAudioMuted] = useState<boolean>(() => playfulAudio.isMuted());
  const [isPeeling, setIsPeeling] = useState<boolean>(false);
  const [isReadyToEnter, setIsReadyToEnter] = useState<boolean>(false);
  const [tamperVersion, setTamperVersion] = useState<number>(0);

  const copyrightContainerRef = useRef<HTMLDivElement>(null);
  const canvasWatermarkRef = useRef<HTMLCanvasElement>(null);
  const stickerCardRef = useRef<HTMLDivElement>(null);

  // 3D Parallax tilt spring motion values
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useSpring(useTransform(mouseY, [-120, 120], [20, -20]), { stiffness: 280, damping: 22 });
  const rotateY = useSpring(useTransform(mouseX, [-120, 120], [-20, 20]), { stiffness: 280, damping: 22 });

  const activeFinish = STICKER_FINISH_PRESETS[activeFinishIndex];
  const activeEmblem = CUTE_EMBLEMS[activeEmblemIndex];
  const activeShape = STICKER_SHAPES[activeShapeIndex];
  const ActiveEmblemIcon = activeEmblem.icon;

  // Progressive loading progress
  useEffect(() => {
    let current = 0;
    const interval = setInterval(() => {
      const increment = current < 30 ? 2.5 : current < 75 ? 3.2 : current < 95 ? 1.8 : 1.2;
      current = Math.min(100, current + increment);
      setProgress(Math.round(current));

      if (current >= 100) {
        setIsReadyToEnter(true);
        clearInterval(interval);
      }
    }, 45);

    return () => clearInterval(interval);
  }, []);

  const onFinishRef = useRef(onFinish);
  useEffect(() => {
    onFinishRef.current = onFinish;
  });

  // Auto exit after complete if user doesn't click
  useEffect(() => {
    if (isReadyToEnter) {
      const autoCloseTimer = setTimeout(() => {
        onFinishRef.current?.();
      }, 4000);
      return () => clearTimeout(autoCloseTimer);
    }
  }, [isReadyToEnter]);

  // Anti-tamper & Canvas security watermark
  useEffect(() => {
    if (canvasWatermarkRef.current) {
      renderCanvasWatermark(canvasWatermarkRef.current, 580, 68);
    }
  }, [tamperVersion]);

  useEffect(() => {
    const cleanup = setupAntiTamperGuard(copyrightContainerRef.current, () => {
      setTamperVersion(v => v + 1);
    });
    return cleanup;
  }, []);

  // Mouse move for interactive 3D tilt
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!stickerCardRef.current) return;
    const rect = stickerCardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    mouseX.set(e.clientX - centerX);
    mouseY.set(e.clientY - centerY);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  // Spawn playful confetti & sparkles on tap
  const triggerPlayfulBurst = () => {
    playfulAudio.playSparkleChime();
    
    const symbols = ['✨', '⭐', '💖', '🌟', '💎', '🎉', '🌸', '🌈'];
    const colors = ['#f59e0b', '#06b6d4', '#ec4899', '#10b981', '#a855f7', '#3b82f6'];
    
    const newParticles: Particle[] = Array.from({ length: 14 }, (_, i) => ({
      id: Date.now() + i,
      x: (Math.random() - 0.5) * 240,
      y: (Math.random() - 0.5) * 240 - 40,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: Math.random() * 16 + 10,
      symbol: symbols[Math.floor(Math.random() * symbols.length)]
    }));

    setParticles(prev => [...prev.slice(-20), ...newParticles]);
    setTimeout(() => {
      setParticles(prev => prev.filter(p => !newParticles.some(np => np.id === p.id)));
    }, 1300);
  };

  const handleFinishChange = (index: number) => {
    setActiveFinishIndex(index);
    playfulAudio.playFinishSwitch();
    triggerPlayfulBurst();
  };

  const handleEmblemChange = (index: number) => {
    setActiveEmblemIndex(index);
    playfulAudio.playCutePop(1.1);
  };

  const handleShapeChange = (index: number) => {
    setActiveShapeIndex(index);
    playfulAudio.playCutePop(0.95);
  };

  const toggleSound = () => {
    const muted = playfulAudio.toggleMuted();
    setIsAudioMuted(muted);
    if (!muted) playfulAudio.playCutePop(1.3);
  };

  const handleLaunch = () => {
    playfulAudio.playLaunchFanfare();
    if (onFinish) onFinish();
  };

  const currentStage = LOADING_STAGES.find(s => progress <= s.threshold) || LOADING_STAGES[LOADING_STAGES.length - 1];
  const StageIcon = currentStage.icon;

  const isLight = theme === 'light';

  return (
    <div 
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`fixed inset-0 z-[99999] overflow-y-auto overflow-x-hidden flex flex-col items-center justify-between select-none px-4 py-4 sm:py-6 font-sans transition-colors duration-300 ${
        isLight 
          ? 'bg-slate-100/98 text-slate-900 backdrop-blur-2xl' 
          : 'bg-zinc-950/98 text-zinc-100 backdrop-blur-2xl'
      }`}
    >
      {/* Background Animated Gradient Mesh */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-40">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-blue-500/10 rounded-full blur-[160px]" />
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none z-30 overflow-hidden">
        {particles.map(p => (
          <motion.div
            key={p.id}
            initial={{ opacity: 1, scale: 0.5, x: '50vw', y: '42vh' }}
            animate={{ 
              opacity: 0, 
              scale: 1.5, 
              x: `calc(50vw + ${p.x}px)`, 
              y: `calc(42vh + ${p.y}px)`,
              rotate: Math.random() * 120 - 60
            }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            style={{ fontSize: `${p.size}px` }}
            className="absolute pointer-events-none drop-shadow-md"
          >
            {p.symbol}
          </motion.div>
        ))}
      </div>

      {/* Top Header Bar */}
      <motion.div 
        initial={{ opacity: 0, y: -15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`w-full max-w-4xl flex items-center justify-between text-xs pb-3 border-b relative z-20 ${
          isLight ? 'border-slate-300/80 text-slate-600' : 'border-zinc-800/80 text-zinc-400'
        }`}
      >
        <div className="flex items-center gap-2.5">
          <AppLogo size={30} animated={true} />
          <div>
            <div className="flex items-center gap-1.5">
              <span className={`font-extrabold tracking-wide text-xs sm:text-sm ${isLight ? 'text-slate-900' : 'text-white'}`}>
                VARIABLE STICKER STUDIO
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500 font-bold border border-blue-500/20">
                PRO 2026
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Audio Mute/Unmute Toggle */}
          <button
            type="button"
            onClick={toggleSound}
            className={`p-1.5 rounded-lg border transition-colors cursor-pointer flex items-center gap-1 text-xs ${
              isLight 
                ? 'bg-white hover:bg-slate-200 border-slate-300 text-slate-700 shadow-xs' 
                : 'bg-zinc-900 hover:bg-zinc-800 border-zinc-700 text-zinc-300'
            }`}
            title={isAudioMuted ? 'Unmute Sound FX' : 'Mute Sound FX'}
          >
            {isAudioMuted ? <VolumeX className="w-3.5 h-3.5 text-zinc-500" /> : <Volume2 className="w-3.5 h-3.5 text-amber-400" />}
          </button>

          {/* Theme Switcher Button */}
          <button
            type="button"
            onClick={toggleTheme}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all cursor-pointer shadow-xs ${
              isLight
                ? 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-800 border-amber-300'
                : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border-zinc-700'
            }`}
            title="Toggle Light / Dark Mode"
          >
            {isLight ? <Sun className="w-3.5 h-3.5 text-amber-500" /> : <Moon className="w-3.5 h-3.5 text-indigo-400" />}
            <span className="hidden sm:inline">{isLight ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          {/* Security badge */}
          <div className="hidden sm:flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 font-semibold">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Verified System</span>
          </div>
        </div>
      </motion.div>

      {/* Center Main Stage: Interactive Playground */}
      <div className="flex flex-col items-center justify-center my-auto w-full max-w-2xl text-center relative z-20 py-2">
        
        {/* Interactive 3D Holographic / Foil Sticker Card */}
        <div 
          ref={stickerCardRef}
          className="relative perspective-1000 mb-3 cursor-pointer select-none"
          onClick={triggerPlayfulBurst}
        >
          <motion.div
            style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
            transition={{ type: 'spring', stiffness: 350, damping: 18 }}
            className="relative group"
            title="Interactive tactile sticker mockup - Tap to trigger sparkle effects!"
          >
            {/* Glowing Foil Ambient Halo */}
            <div 
              className={`absolute -inset-3 ${activeShape.radius} bg-gradient-to-r ${activeFinish.gradient} opacity-50 blur-xl group-hover:opacity-85 transition-opacity duration-300`} 
            />

            {/* Sticker Body */}
            <div className={`relative w-36 h-36 sm:w-44 sm:h-44 ${activeShape.radius} ${
              isLight ? 'bg-white/95 border-2 border-slate-200 shadow-xl' : 'bg-zinc-900/95 border-2 border-zinc-700/80 shadow-2xl'
            } ${activeFinish.ring} ring-2 ring-offset-4 ${
              isLight ? 'ring-offset-slate-100' : 'ring-offset-zinc-950'
            } flex flex-col items-center justify-center p-3 overflow-hidden transition-all duration-300`}>

              {/* Holographic Specular Shimmer Beam */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out pointer-events-none" />

              {/* Printing Registration Marks */}
              <div className="absolute top-1.5 left-1.5 text-[9px] text-zinc-500 font-mono select-none">✛</div>
              <div className="absolute top-1.5 right-1.5 text-[9px] text-zinc-500 font-mono select-none">✛</div>
              <div className="absolute bottom-1.5 left-1.5 text-[9px] text-zinc-500 font-mono select-none">✛</div>
              <div className="absolute bottom-1.5 right-1.5 text-[9px] text-zinc-500 font-mono select-none">✛</div>

              {/* Center Emblem with Interactive Finish */}
              <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-tr ${activeFinish.gradient} flex items-center justify-center text-zinc-950 shadow-lg mb-1 transform transition-transform group-hover:rotate-6 group-hover:scale-110 duration-200`}>
                <ActiveEmblemIcon className="w-6 h-6 sm:w-7 sm:h-7 stroke-[2.5]" />
              </div>

              {/* Live Editable Text Label */}
              <div 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingText(true);
                }}
                className="flex items-center gap-1 cursor-text group/edit"
                title="Click to customize text on this sticker"
              >
                <span className={`text-[11px] sm:text-xs font-black tracking-wider uppercase ${activeFinish.textColor} drop-shadow-xs truncate max-w-[130px]`}>
                  {customText}
                </span>
                <Edit3 className="w-2.5 h-2.5 opacity-0 group-hover/edit:opacity-100 transition-opacity text-zinc-400" />
              </div>

              <span className="text-[8px] font-bold text-zinc-400 mt-0.5 flex items-center gap-1">
                <Sparkles className="w-2 h-2 text-amber-400 animate-spin" />
                <span>Tap for Sparkles</span>
              </span>

              {/* Peeling Corner Touch Effect */}
              <div 
                onMouseEnter={() => setIsPeeling(true)}
                onMouseLeave={() => setIsPeeling(false)}
                className={`absolute top-0 right-0 w-8 h-8 transition-transform duration-300 pointer-events-auto ${
                  isPeeling ? 'scale-125' : ''
                }`}
              >
                <div className={`w-full h-full bg-gradient-to-bl ${
                  isLight ? 'from-slate-200 via-slate-300 to-transparent' : 'from-zinc-700 via-zinc-800 to-transparent'
                } border-b border-l ${
                  isLight ? 'border-slate-300 shadow-sm' : 'border-zinc-600 shadow-md'
                }`} />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick Text Preset Chips */}
        <div className="flex flex-wrap items-center justify-center gap-1 mb-3 max-w-md">
          {QUICK_TAG_SUGGESTIONS.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => {
                setCustomText(tag);
                playfulAudio.playCutePop(1.15);
                triggerPlayfulBurst();
              }}
              className={`px-2 py-0.5 rounded-full text-[9px] font-bold transition-all cursor-pointer ${
                customText === tag
                  ? 'bg-blue-600 text-white shadow-xs'
                  : `${isLight ? 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100' : 'bg-zinc-900 text-zinc-400 border border-zinc-800 hover:text-white'}`
              }`}
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Playful Interactive Mini Toolbar (Finishes, Emblems & Shapes) */}
        <div className={`flex flex-wrap items-center justify-center gap-1.5 p-1.5 rounded-2xl border mb-3 backdrop-blur-md max-w-xl ${
          isLight ? 'bg-white/80 border-slate-300 shadow-sm' : 'bg-zinc-900/80 border-zinc-800 shadow-lg'
        }`}>
          {/* Finish Presets */}
          <div className="flex items-center gap-1 pr-1.5 border-r border-zinc-700/50">
            {STICKER_FINISH_PRESETS.map((f, i) => (
              <button
                key={f.id}
                type="button"
                onClick={() => handleFinishChange(i)}
                className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  activeFinishIndex === i
                    ? `${isLight ? 'bg-slate-900 text-white shadow-xs' : 'bg-zinc-800 text-white ring-1 ring-zinc-500 shadow-xs'}`
                    : `${isLight ? 'text-slate-600 hover:bg-slate-100' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/60'}`
                }`}
                title={`Switch to ${f.name}`}
              >
                <span className={`w-2 h-2 rounded-full ${f.accent}`} />
                <span>{f.tag}</span>
              </button>
            ))}
          </div>

          {/* Emblem Choosers */}
          <div className="flex items-center gap-1">
            {CUTE_EMBLEMS.map((em, i) => {
              const Icon = em.icon;
              return (
                <button
                  key={em.id}
                  type="button"
                  onClick={() => handleEmblemChange(i)}
                  className={`p-1.5 rounded-lg transition-all cursor-pointer ${
                    activeEmblemIndex === i
                      ? 'bg-blue-600 text-white shadow-xs scale-105'
                      : `${isLight ? 'text-slate-500 hover:bg-slate-200' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`
                  }`}
                  title={em.name}
                >
                  <Icon className="w-3.5 h-3.5" />
                </button>
              );
            })}
          </div>

          {/* Shape Choosers */}
          <div className="flex items-center gap-1 pl-1.5 border-l border-zinc-700/50">
            {STICKER_SHAPES.map((sh, i) => (
              <button
                key={sh.id}
                type="button"
                onClick={() => handleShapeChange(i)}
                className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase transition-all cursor-pointer ${
                  activeShapeIndex === i
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : `${isLight ? 'text-slate-500 hover:bg-slate-200' : 'text-zinc-500 hover:text-zinc-300'}`
                }`}
              >
                {sh.name}
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic Title */}
        <motion.h1 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight flex items-center justify-center gap-2 mb-1"
        >
          <span>Variable Sticker</span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-500 to-amber-500">
            Studio
          </span>
        </motion.h1>

        <p className={`text-xs sm:text-sm max-w-md mx-auto mb-3 leading-relaxed font-medium ${
          isLight ? 'text-slate-600' : 'text-zinc-400'
        }`}>
          Industrial Variable Data Imposition, Luxury Substrates & 3D Packaging Simulator.
        </p>

        {/* Progress Bar & Engine Status */}
        <div className="w-full max-w-md space-y-2 mb-3">
          <div className="flex items-center justify-between text-xs px-1">
            <span className={`font-semibold flex items-center gap-1.5 truncate text-[11px] sm:text-xs ${
              isLight ? 'text-slate-700' : 'text-zinc-300'
            }`}>
              <StageIcon className="w-3.5 h-3.5 text-blue-500 animate-spin-slow shrink-0" />
              <span className="truncate">{currentStage.label}</span>
            </span>
            <span className="font-mono font-bold text-blue-500 shrink-0 ml-2">
              {progress}%
            </span>
          </div>

          {/* Glowing Animated Progress Track */}
          <div className={`h-2.5 w-full rounded-full overflow-hidden p-0.5 relative shadow-inner ${
            isLight ? 'bg-slate-200 border border-slate-300' : 'bg-zinc-900 border border-zinc-800'
          }`}>
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-blue-600 via-indigo-500 to-amber-400 relative"
              style={{ width: `${progress}%` }}
              transition={{ ease: 'linear' }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-[0_0_12px_rgba(255,255,255,0.9)]" />
            </motion.div>
          </div>
        </div>

        {/* Launch CTA */}
        <div className="h-11 flex items-center justify-center">
          {progress >= 40 ? (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={handleLaunch}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold text-xs shadow-xl shadow-indigo-600/30 flex items-center gap-2 cursor-pointer border border-indigo-400/40"
            >
              <Smile className="w-4 h-4 text-amber-300" />
              <span>{progress >= 100 ? 'Launch Workspace' : 'Enter Workspace Now'}</span>
              <ChevronRight className="w-4 h-4" />
            </motion.button>
          ) : (
            <div className={`flex items-center gap-2 text-xs font-mono ${
              isLight ? 'text-slate-500' : 'text-zinc-500'
            }`}>
              <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-500" />
              <span>Calibrating vector matrices...</span>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section: PROMINENT & HIGH-SECURITY DIGITAL COPYRIGHT NOTICE */}
      <div className="w-full max-w-2xl flex flex-col items-center gap-2 relative z-20">
        
        {/* Anti-Tamper Guarded Container with MutationObserver Protection */}
        <div 
          ref={copyrightContainerRef}
          id="vss-author-security-watermark"
          data-security-hash={SECURITY_TOKEN}
          className={`w-full rounded-xl p-3 sm:p-3.5 shadow-xl backdrop-blur-md flex flex-col gap-1.5 transition-all relative overflow-hidden border ${
            isLight 
              ? 'bg-white/95 border-amber-500/40 text-slate-800 shadow-slate-300/50' 
              : 'bg-zinc-900/90 border-amber-500/30 text-zinc-100 shadow-2xl'
          }`}
          style={{ display: 'flex' }}
        >
          {/* Top Gold Highlight Bar */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-90" />

          {/* Primary Copyright Badge with Verified Author */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-500 border border-amber-500/30 shrink-0 shadow-xs">
                <ShieldCheck className="w-4 h-4" />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5 flex-wrap justify-center sm:justify-start">
                  <span className={`text-xs sm:text-sm font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`}>
                    {FULL_COPYRIGHT_TEXT}
                  </span>
                  <span className="px-1.5 py-0.2 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-[9px] font-bold border border-emerald-500/30 flex items-center gap-0.5">
                    <CheckCircle2 className="w-2.5 h-2.5" /> Verified
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Short Security Legal Notice */}
          <p className={`text-[10px] leading-relaxed text-center sm:text-left ${
            isLight ? 'text-slate-600' : 'text-zinc-400'
          }`}>
            {SHORT_COPYRIGHT_NOTICE}
          </p>

          {/* Cryptographic Integrity Signature Stamp */}
          <div className={`pt-1 border-t flex items-center justify-between text-[9px] font-mono ${
            isLight ? 'border-slate-200 text-slate-500' : 'border-zinc-800/80 text-zinc-500'
          }`}>
            <div className="flex items-center gap-1.5">
              <Lock className="w-3 h-3 text-amber-500" />
              <span>DIGITAL WATERMARK ID: <strong className={isLight ? 'text-slate-700' : 'text-zinc-300'}>{SECURITY_SIGNATURE_ID}</strong></span>
            </div>
            <span className="hidden sm:inline">
              TAMPER PROTECTION ACTIVE • IMMUTABLE DOM GUARD
            </span>
          </div>
        </div>

        {/* Hidden High-DPI Canvas Watermark */}
        <canvas
          ref={canvasWatermarkRef}
          id="vss-canvas-security-stamp"
          className="hidden"
          width="580"
          height="68"
        />

        <span className={`text-[9px] font-mono text-center ${isLight ? 'text-slate-500' : 'text-zinc-600'}`}>
          Secured by Dynamic Cryptographic Integrity Engine • Version 2026.1
        </span>
      </div>

    </div>
  );
};
